import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import DataGrid from '@afosto/app-components-react/DataGrid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Hidden from '@material-ui/core/Hidden';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import { shipmentFormatter } from './formatters';
import translations from './translations';
import DataList from '../DataList';
import ShipmentListItem from '../ShipmentsListItem';

const fetchShipment = async shipmentId => {
  try {
    const shipmentResponse = await shipmentsApi.viewShipment(shipmentId);
    const { data: shipment } = shipmentResponse;

    return shipment;
  } catch (err) {
    return null;
  }
};

const ShipmentsSection = ({ shipmentIds }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['lcs:shipments:read']);

  const fetchShipments = async () => {
    try {
      const promises = shipmentIds.map(shipmentId => fetchShipment(shipmentId));
      const responses = await Promise.allSettled(promises).then(result =>
        result.filter(promise => promise.status === 'fulfilled'),
      );

      return responses.reduce((acc, response) => {
        if (response?.value) {
          return [...acc, response.value];
        }

        return acc;
      }, []);
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchShipments, {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shipments.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'shipment',
        },
        {
          key: 'is_outbound',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shipments.isOutbound',
            defaultMessage: 'Outbound',
            description: 'The column name for column is outbound',
          }),
          type: 'boolean',
        },
        {
          key: 'is_finished',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shipments.isFinished',
            defaultMessage: 'Finished',
            description: 'The column name for column is finished',
          }),
          type: 'boolean',
        },
        {
          key: 'expected_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shipments.expectedAt',
            defaultMessage: 'Expected at',
            description: 'The column name for column expected at',
          }),
          type: 'datetime',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shipments.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });

  const dataSourceRef = useRef(dataSource);
  const shipmentIdsString = shipmentIds.join(',');

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    dataSourceRef.current.reloadDataSource();
  }, [shipmentIdsString]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.page.shipmentsSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.page.shipmentsSection.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={item => <ShipmentListItem shipment={item} key={item.id} />}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid dataSource={dataSource} formatters={{ shipment: shipmentFormatter }} />
      </Hidden>
    </Card>
  );
};

ShipmentsSection.propTypes = {
  shipmentIds: PropTypes.array,
};

ShipmentsSection.defaultProps = {
  shipmentIds: [],
};

export default ShipmentsSection;
